import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { EditGlobalFiltersLink } from 'components/EditGlobalFiltersLink'
import {
  CardActionButton,
  CardActions,
  CardBody,
  CardRoot,
  CardTitleRow,
  FilterChip,
  GroupPrefix,
  RevealOnCardHover,
} from 'components/filter-card/filter-card.styles'
import { FilterCardAccentBar } from 'components/FilterCardAccentBar'
import { InheritedFilterBadge } from 'components/InheritedFilterBadge'
import { StaleFilterBadge } from 'components/StaleFilterBadge'
import { TooltipIconButton } from 'components/TooltipIconButton'
import { OperatorPopover } from 'features/charts-v2/components/FilterOperatorPopover'
import { FilterPopover } from 'features/charts-v2/components/FilterPopover'
import { FilterRangePopover } from 'features/charts-v2/components/FilterRangePopover/FilterRangePopover'
import { RatingTypePopover } from 'features/charts-v2/components/RatingTypePopover'
import {
  type NumericOperatorType,
  type OperatorType,
  type SelectedFilterOption,
} from 'features/charts-v2/types'
import { rollUpCustomerGroupNames } from 'features/charts-v2/utils/customer-group-hierarchy'
import { restrictionForSelection } from 'features/charts-v2/utils/derive-filter-restriction'
import {
  collapseThemeDisplayName,
  deduplicateFilterValues,
} from 'features/charts-v2/utils/filter-dedup'
import { getFilterGroupLabel } from 'features/charts-v2/utils/filter-sidebar-groups'
import { restrictSelectValues } from 'features/charts-v2/utils/restrict-filter-options'
import { rollUpThemeDisplayNames } from 'features/charts-v2/utils/theme-hierarchy'
import {
  FilterOption,
  FilterOptionRange,
  FilterOptionSelect,
  FilterOptionValue,
  FilterTypes,
} from 'features/filters/types'
import {
  ALIAS_FOR_NULL_VALUES,
  isNullValueAlias,
} from 'src/constants/filter-values'

const NULL_VALUE = 'Null'
const RATINGS_ONLY_VALUE_TYPE = 2

// The header row's two flex clusters (title+badge, stale+actions) — see the
// "Title tightly grouped..." comment where they're used. `$gap` is the only
// thing that differs between the two.
const HeaderGroup = styled.div<{ $gap: string }>`
  display: flex;
  align-items: center;
  gap: ${({ $gap }) => $gap};
`

interface FilterCardProps {
  filter: FilterOption
  updateFilterSelection: (
    field: string,
    operator: OperatorType | NumericOperatorType,
    values: string[],
    rangeData?: {
      min?: number
      max?: number
      valueType?: number
      numericValue?: number
    },
  ) => void
  getFilterSelection: (field: string) => SelectedFilterOption | null
  // Presentation knobs bundled into one prop to keep this component's own
  // prop count down (mirrors the `ghost` bundle below and
  // `SelectedFilterOptionListProps`'s `presentation` bundle).
  presentation?: {
    // Whether the `Model >` prefix renders in the card title. Default true.
    showBreakdownHierarchy?: boolean
    // Locks the operator to whatever `getFilterSelection` returns: the
    // operator span renders as static text and OperatorPopover never opens.
    // Value editing and removal stay live — unlike `inherited`, which
    // freezes the whole card. Set by callers whose persistence format has
    // no operator field (e.g. the agent create form's SavedFilters.criteria,
    // which is always implicitly "is"). Default false: no existing caller
    // changes.
    lockOperator?: boolean
    // Names a selected value the filter options can't. A theme's options
    // list only clusters with counted posts, so a saved uuid for a live
    // cluster with none yet is absent from them; the caller supplies the
    // server's name for it. Consulted only after the option lookup misses.
    getValueLabel?: (field: string, value: string) => string | undefined
    // The parent scope's (dashboard's) selection for THIS field, for an owned
    // card whose field the parent also filters. An inherited card already
    // carries it as `inherited.selection`, so this is only needed on the
    // tile's own override card. Either source caps the value popover at the
    // parent's values: a tile may only narrow the dashboard, never offer a
    // value the dashboard has filtered out. Absent (every non-dashboard
    // caller) means no cap.
    globalSelection?: SelectedFilterOption
  }
  // The ghost (eye-toggled-off) controls, bundled into one prop to keep this
  // component's own prop count down (mirrors `SelectedFilterOptionListProps`'s
  // `ghost` bundle). Presence means ghosting is supported in this rendering
  // context (always true for an owned card in the dashboards sidebar);
  // `disabledSelection` presence within it means THIS field is currently
  // ghosted — it no longer lives in `panel.filters` (identical to pressing
  // the ✕), and is the stashed definition the card renders read-only until
  // re-enabled via `onToggleDisabled`. `onRemoveGhost` permanently discards
  // a disabled filter's card (the ✕ while disabled).
  ghost?: {
    disabledSelection?: SelectedFilterOption
    onToggleDisabled: () => void
    onRemoveGhost: () => void
  }
  // Presence means this card renders a filter inherited from a parent scope
  // (a dashboard's global filters) rather than one the tile owns. The
  // operator stays frozen and the eye/✕ buttons are hover targets with no
  // click handler of their own — only the "Edit global filters" link inside
  // their shared tooltip calls `onEditSource` — but the VALUE chip stays
  // clickable as long as the card isn't superseded, opening the same
  // FilterPopover an owned card would, narrowed to this filter's own values
  // (see `restrictFilterOptions`). Picking a subset there creates a
  // tile-owned override, which is what turns this card superseded (crossed
  // out).
  // Separate axis from `disabledSelection` (the ghost/eye-toggled-off
  // state): a ghost is a tile's OWN filter the user chose to stash, while an
  // inherited filter was never the tile's to remove in the first place.
  inherited?: {
    selection: SelectedFilterOption
    // null = this parent filter reaches the tile's query unchanged.
    // A string = it doesn't (the tile overrides/narrows it, or is
    // unsynced), and this explains why, verbatim, in the badge tooltip.
    supersededNote: string | null
    // Still supplied by every caller and still read by
    // `deriveFilterRestriction` — but no longer forwarded into
    // `InheritedFilterBadge`, whose default message stopped templating off
    // it (see that component's doc comment).
    sourceLabel: string
    // Focuses the parent scope's own filter controls (e.g. the dashboard's
    // Global filters panel). Present on every dashboard-tile caller. The
    // eye/✕ buttons themselves are pure hover targets with no click handler
    // — clicking them does nothing; only the clickable link rendered
    // inside their shared tooltip calls this. Without it, that tooltip
    // renders as plain, non-clickable text. This card never removes or
    // disables the parent's filter either way, only overrides its values.
    onEditSource?: () => void
  }
  // The ownership accent bar, bundled into one prop to keep this component's
  // own prop count down (mirrors the `ghost`/`inherited`/`presentation`
  // bundles above/below) — its three fields are `FilterCardAccentBar`'s own
  // props verbatim, since that's the only thing this bundle configures.
  // Presence of `accent` is what decides whether the bar renders at all:
  // 'local' (orange) for the tile's own filter, 'global' (navy) for one
  // inherited from a parent scope. Independent of `inherited` above (which
  // drives read-only behavior): a card can be owned-with-accent without
  // being read-only. Omitted entirely outside a dashboard-tile context
  // (charts/feedback/keywords keep the plain card).
  //
  // `onEditSource` here is independent of `inherited.onEditSource` above:
  // that one only exists on an inherited row, this one is forwarded to the
  // bar regardless of whether the row is owned or inherited (an owned row
  // can still carry `accent: 'global'` — the dashboard's own Global filters
  // panel — where this is naturally `undefined`; see
  // `FilterCardAccentBar`'s doc comment for why the bar treats that as
  // "nothing to jump to" rather than a bug).
  accentBar?: {
    accent: 'local' | 'global'
    tileKind?: 'chart' | 'widget'
    onEditSource?: () => void
  }
  // Presence (with a non-empty `values`) means this card's stored selection
  // includes at least one value the project no longer offers (a renamed or
  // deleted brand, a removed field option). Read-only — no toggle, no click
  // target — distinct from `ghost`'s Disable affordance. Renders an
  // always-visible warning badge; the authoritative "runs will fail"
  // explanation lives in the caller's own stale-filters notice, not here.
  stale?: {
    values: readonly string[]
    labels: Record<string, string>
  }
}

export const FilterCard: React.FC<FilterCardProps> = ({
  filter,
  updateFilterSelection,
  getFilterSelection,
  presentation,
  ghost,
  inherited,
  accentBar,
  stale,
}) => {
  const {
    showBreakdownHierarchy = true,
    lockOperator = false,
    getValueLabel,
    globalSelection,
  } = presentation ?? {}
  // Tracks hover on the value chip solely to gate the "click to select
  // filter values" tooltip below — the chip's own highlight is CSS `:hover`
  // via `FilterChip` now, not this state.
  const [isFieldValueHovered, setIsFieldValueHovered] = useState(false)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isOperatorDropdownOpen, setIsOperatorDropdownOpen] = useState(false)
  const [showRatingTypePopover, setShowRatingTypePopover] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const operatorRef = useRef<HTMLSpanElement>(null)

  const isDisabled = ghost?.disabledSelection != null
  const isReadOnly = inherited != null
  const isSuperseded = inherited?.supersededNote != null
  // Whether this card's eye/✕ actually disable/remove anything, and whether
  // its operator can change. Both are false for every inherited card — its
  // eye/✕ render (see below) purely as hover targets for their shared
  // tooltip's `onEditSource` link, with no click handler of their own, and
  // its operator always stays frozen. Only the VALUE chip has a separate,
  // narrower interactivity flag (`isValueInteractive` below), since editing
  // an inherited card's values is how a tile creates its own override.
  const isInteractive = !isDisabled && !isReadOnly
  // Value-chip interactivity: an inherited card that ISN'T superseded can
  // still have its values edited — doing so creates the tile's own override
  // (see the `inherited` doc comment on `FilterCardProps`). Once superseded,
  // this card's values are inert; the new override card (a separate, fully
  // owned FilterCard) is where further editing happens.
  const isValueInteractive = !isDisabled && !isSuperseded
  // Operator-specific interactivity — the operator stays frozen on every
  // inherited card, superseded or not (only VALUES can create an override).
  // See the `lockOperator` doc comment on `FilterCardProps`.
  const isOperatorInteractive = isInteractive && !lockOperator

  // Close any popover that was already open the moment its governing
  // interactivity flag flips false. The render-time `&&` guards below cover
  // the single frame before this effect runs.
  useEffect(() => {
    if (!isValueInteractive) {
      setIsPopoverOpen(false)
      setShowRatingTypePopover(false)
    }
    if (!isOperatorInteractive) {
      setIsOperatorDropdownOpen(false)
    }
  }, [isValueInteractive, isOperatorInteractive])

  // Get current selection from global state, or the stashed ghost/inherited
  // value while disabled/read-only.
  const currentSelection =
    ghost?.disabledSelection ??
    inherited?.selection ??
    getFilterSelection(filter.field)
  const selectedValues = currentSelection?.values || []
  const selectedOperator = (currentSelection?.operator || 'is') as OperatorType

  // Helper to get display name for a filter value
  // For theme and dataset filters, looks up the name by cluster_uuid
  const getDisplayValue = (value: string): string => {
    if (
      (filter.field === 'theme' || filter.field === 'proj_uuid') &&
      filter.filter_type === FilterTypes.SELECT
    ) {
      const selectFilter = filter as FilterOptionSelect
      // Look up name by cluster_uuid
      const option = selectFilter.values?.find((v) => v.cluster_uuid === value)
      const name =
        option?.value || getValueLabel?.(filter.field, value) || value

      // For themes, collapse "Parent: Parent" → "Parent" (matches V1 behavior)
      if (filter.field === 'theme') {
        return collapseThemeDisplayName(name)
      }
      return name
    }
    if (isNullValueAlias(value)) {
      return ALIAS_FOR_NULL_VALUES
    }
    return value
  }

  const getFieldValue = (filter: FilterOption): string => {
    // TODO: If length>36, cut off at length=36 and append `...`

    // Map selected values to display names (handles theme UUID -> name lookup)
    let displayValues = selectedValues.map(getDisplayValue)

    // For themes, de-duplicate by display name to match V1 behavior and roll
    // a fully-selected parent up to its own name. Multiple cluster_uuids can
    // share a display name, so the roll-up runs on name-deduplicated options.
    if (filter.field === 'theme') {
      const themeOptionValues =
        filter.filter_type === FilterTypes.SELECT
          ? deduplicateFilterValues((filter as FilterOptionSelect).values ?? [])
          : []
      displayValues = rollUpThemeDisplayNames(displayValues, themeOptionValues)
    }

    // A fully-selected My / Competitor brand or product group reads as its
    // label, the same roll-up themes get above.
    displayValues = rollUpCustomerGroupNames(displayValues, filter)

    if (displayValues.length === 1) {
      return displayValues[0]!
    }

    if (displayValues.length === 2) {
      return displayValues.join(', ')
    }

    if (displayValues.length > 2) {
      return `${displayValues.slice(0, 2).join(', ')}, or ${displayValues.length - 2} more`
    }

    if (filter.filter_type === FilterTypes.SELECT) {
      const selectFilter = filter as FilterOptionSelect
      return selectFilter.values?.[0]?.value || 'No values'
    } else if (filter.filter_type === FilterTypes.WINDOW) {
      // Window filters (e.g. rating) show the *selected* range, falling back
      // to the available bounds when no selection has been made yet.
      const rangeFilter = filter as FilterOptionRange
      const min = currentSelection?.min ?? rangeFilter.min
      const max = currentSelection?.max ?? rangeFilter.max
      return `${min} - ${max}`
    } else if (filter.filter_type === FilterTypes.DATE) {
      return `${filter.min} - ${filter.max}`
    }
    return 'N/A'
  }

  const handleSelectionChange = (newSelectedValues: string[]): void => {
    updateFilterSelection(filter.field, selectedOperator, newSelectedValues)
  }

  const handleOperatorChange = (newOperator: OperatorType): void => {
    updateFilterSelection(filter.field, newOperator, selectedValues)
  }

  const handleDeleteFilter = (): void => {
    // Remove the filter by passing an empty values array
    updateFilterSelection(filter.field, selectedOperator, [])
  }

  // Tooltip content for the inherited-card eye/✕ (which don't disable/remove
  // anything themselves) — each names the action it can't do, then the
  // shared `EditGlobalFiltersLink` (a clickable link when the caller wired
  // `onEditSource`, otherwise plain text). Computed unconditionally since
  // it's cheap; only rendered when `isReadOnly`.
  const disableTooltip = (
    <>
      Cannot disable a global filter here.{' '}
      <EditGlobalFiltersLink onClick={inherited?.onEditSource} />
    </>
  )
  const removeTooltip = (
    <>
      Cannot remove a global filter here.{' '}
      <EditGlobalFiltersLink onClick={inherited?.onEditSource} />
    </>
  )

  // For WINDOW filters (rating), synthesize a SELECT-shaped filter so the
  // standard FilterPopover can render it as a checkbox list. Each integer
  // between min and max becomes a checkable option.
  const isWindowFilter = filter.filter_type === FilterTypes.WINDOW
  // Any other window is a numeric custom field (e.g. a price): it gets a
  // min/max range, since stepping min..max into checkboxes invents values.
  const isRatingWindow =
    isWindowFilter &&
    (filter.field === 'score' || filter.field === 'post_score')
  const isRangeWindow = isWindowFilter && !isRatingWindow
  const syntheticSelectFilter = useMemo<FilterOptionSelect | null>(() => {
    if (!isRatingWindow) return null
    const rangeFilter = filter as FilterOptionRange
    const min = rangeFilter.min ?? 1
    const max = rangeFilter.max ?? 5
    const step = rangeFilter.step ?? 1
    const values: FilterOptionValue[] = []
    for (let i = min; i <= max; i += step) {
      values.push({ value: String(i), identifiers: [String(i)] })
    }
    return {
      ...rangeFilter,
      filter_type: FilterTypes.SELECT,
      values,
    } as FilterOptionSelect
  }, [filter, isRatingWindow])

  // The popover's option list, capped at the parent scope's values when the
  // parent filters this field with a value list. "Does this selection
  // constrain a value list?" is `restrictionForSelection`'s call — the one
  // definition `deriveFilterRestriction` also uses, so the two can't drift.
  // Enforced here rather than trusting the caller to have pre-narrowed
  // `filter`. Currently-selected values stay listed so they can be unchecked.
  const parentSelection = inherited?.selection ?? globalSelection
  const popoverFilter = useMemo<FilterOption>(() => {
    const cap = parentSelection && restrictionForSelection(parentSelection)
    if (filter.filter_type !== FilterTypes.SELECT || cap?.kind !== 'values') {
      return filter
    }
    return restrictSelectValues(
      filter,
      cap.allowed,
      new Set(currentSelection?.values),
    )
    // Keyed on `currentSelection?.values` (a stable reference), not the
    // `selectedValues` fallback array: `FilterPopover` resets its draft
    // whenever `filter` changes identity while open, so this memo must not
    // recompute on every render.
  }, [filter, parentSelection, currentSelection?.values])

  // Resolve which rating boxes the FilterPopover should render as checked.
  // Discrete numeric values in `currentSelection.values` (saved by this
  // popover for non-contiguous selections like "1 and 5 only") take
  // precedence; otherwise fall back to the [min..max] range saved by the
  // sidebar slider.
  const windowSelectedValues = useMemo<string[]>(() => {
    if (!isRatingWindow) return []
    const discrete = (currentSelection?.values || []).filter(
      (v) => v !== 'Null' && !Number.isNaN(Number(v)),
    )
    if (discrete.length > 0) return discrete
    const rangeFilter = filter as FilterOptionRange
    const min = currentSelection?.min ?? rangeFilter.min ?? 1
    const max = currentSelection?.max ?? rangeFilter.max ?? 5
    const step = rangeFilter.step ?? 1
    const values: string[] = []
    for (let i = min; i <= max; i += step) {
      values.push(String(i))
    }
    return values
  }, [
    filter,
    isRatingWindow,
    currentSelection?.values,
    currentSelection?.min,
    currentSelection?.max,
  ])

  // Rating type display + edit. valueType 0/1/2 ↔ Reviews & Ratings /
  // Reviews Only / Ratings Only.
  const RATING_TYPE_LABELS: Record<number, string> = {
    0: 'Reviews & Ratings',
    1: 'Reviews Only',
    2: 'Ratings Only',
  }
  const ratingTypeLabel =
    RATING_TYPE_LABELS[currentSelection?.valueType ?? 0] ?? 'Reviews & Ratings'
  const includesNoStarReviews =
    filter.field === 'score' &&
    currentSelection?.valueType !== RATINGS_ONLY_VALUE_TYPE &&
    selectedValues.includes(NULL_VALUE)
  const getRatingValues = (valueType: number): string[] => {
    if (filter.field !== 'score') return selectedValues
    const valuesWithoutNull = selectedValues.filter((v) => v !== NULL_VALUE)
    if (valueType === RATINGS_ONLY_VALUE_TYPE) return valuesWithoutNull
    return [...valuesWithoutNull, NULL_VALUE]
  }

  const handleRatingTypeChange = (newValueType: number): void => {
    const rangeFilter = filter as FilterOptionRange
    // Store *replaces* the filter on update; carry current min/max so we
    // don't drop the range when changing only the type.
    updateFilterSelection(
      filter.field,
      selectedOperator,
      getRatingValues(newValueType),
      {
        min: currentSelection?.min ?? rangeFilter.min,
        max: currentSelection?.max ?? rangeFilter.max,
        valueType: newValueType,
      },
    )
  }

  const ratingRangeText = (() => {
    if (!isWindowFilter) return ''
    // Prefer discrete values for display when present; render contiguous
    // runs as "min - max" and non-contiguous as a comma list.
    const discrete = (currentSelection?.values || [])
      .filter((v) => v !== 'Null')
      .map((v) => Number(v))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b)
    if (discrete.length > 0) {
      if (discrete.length === 1) return String(discrete[0])
      const isContiguous = discrete.every(
        (n, i) => i === 0 || n === discrete[i - 1]! + 1,
      )
      if (isContiguous) {
        return `${discrete[0]} - ${discrete[discrete.length - 1]}`
      }
      return discrete.join(', ')
    }
    const rangeFilter = filter as FilterOptionRange
    const min = currentSelection?.min ?? rangeFilter.min
    const max = currentSelection?.max ?? rangeFilter.max
    return `${min} - ${max}`
  })()

  const customRangeText = (() => {
    const min = currentSelection?.min
    const max = currentSelection?.max
    if (min !== undefined && max !== undefined) return `${min} – ${max}`
    if (min !== undefined) return `at least ${min}`
    if (max !== undefined) return `at most ${max}`
    // A filter saved as a value list (before ranges) still filters by it.
    if (selectedValues.length > 0) return getFieldValue(filter)
    return 'any value'
  })()

  // Save the checked ratings as discrete numeric values in `values`
  // (e.g. ['1','5']) so non-contiguous selections are expressible. The
  // backend's emit_score_filter (chart_transpiler/emit/filters.py) emits
  // EQ/IN(POST_SCORE, ...) when these are present. We deliberately do
  // NOT send min/max — those would re-introduce the lossy
  // contiguous-range representation. Preserve 'Null' and valueType.
  const handleWindowSelectionChange = (newSelectedValues: string[]): void => {
    const numericValues = newSelectedValues
      .map((v) => Number(v))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b)
    const preservedValues = selectedValues.filter((v) =>
      Number.isNaN(Number(v)),
    )
    if (numericValues.length === 0) {
      if (preservedValues.length === 0) {
        updateFilterSelection(filter.field, selectedOperator, [])
        return
      }
      updateFilterSelection(filter.field, selectedOperator, preservedValues, {
        valueType: currentSelection?.valueType,
      })
      return
    }
    const allValues = [...numericValues.map(String), ...preservedValues]
    updateFilterSelection(filter.field, selectedOperator, allValues, {
      valueType: currentSelection?.valueType,
    })
  }

  return (
    <CardRoot
      ref={cardRef}
      $disabled={isDisabled}
      $accent={accentBar?.accent}
      $superseded={isSuperseded}
    >
      {accentBar && <FilterCardAccentBar {...accentBar} />}
      <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
        <div style={{ width: '100%' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {/* Title tightly grouped with the lock badge (when read-only) —
                the two flex groups on this row (this one, and the
                stale/actions group below) are what put ALL the slack from
                `justify-content: space-between` into one big gap between
                them, instead of spreading it across every item. */}
            <HeaderGroup $gap="6px">
              <CardTitleRow $disabled={isDisabled} $superseded={isSuperseded}>
                <span
                  style={{
                    fontWeight: '500',
                    color: '#374151',
                    fontSize: '14px',
                  }}
                >
                  {showBreakdownHierarchy && (
                    <GroupPrefix>
                      {getFilterGroupLabel(
                        filter.field,
                        'model' in filter ? filter.model : undefined,
                      )}{' '}
                      {'> '}
                    </GroupPrefix>
                  )}
                  {filter.alias || filter.field}
                </span>
              </CardTitleRow>

              {/* Read-only cards additionally get a lock badge the owned
                  card doesn't — see the `inherited` doc comment. */}
              {isReadOnly && (
                <RevealOnCardHover>
                  <InheritedFilterBadge onEditSource={inherited.onEditSource} />
                </RevealOnCardHover>
              )}
            </HeaderGroup>

            <HeaderGroup $gap="4px">
              {/* Always-visible warning badge — see the `stale` doc comment. */}
              {stale && stale.values.length > 0 && (
                <StaleFilterBadge values={stale.values} labels={stale.labels} />
              )}

              {/* Inherited cards' eye/✕ don't disable or remove the parent's
                  filter, and clicking them does nothing at all — they're
                  pure hover targets for their shared tooltip, whose own
                  "Edit global filters" link is the only clickable part. */}
              {isReadOnly && (
                <RevealOnCardHover>
                  <CardActions>
                    <TooltipIconButton
                      tooltipTitle={disableTooltip}
                      height={20}
                      width={20}
                      variant="muted"
                      ariaLabel="Inherited filter"
                      icon={<VisibilityOffIcon />}
                    />
                    <Tooltip content={removeTooltip}>
                      <CardActionButton
                        type="button"
                        $variant="muted"
                        aria-label="Inherited filter"
                      >
                        ×
                      </CardActionButton>
                    </Tooltip>
                  </CardActions>
                </RevealOnCardHover>
              )}

              {/* Eye + Remove buttons - only visible on card hover, via CSS */}
              {!isReadOnly && (
                <RevealOnCardHover>
                  <CardActions>
                    {ghost && (
                      <TooltipIconButton
                        tooltipTitle={isDisabled ? 'Enable' : 'Disable'}
                        height={20}
                        width={20}
                        variant="muted"
                        ariaLabel={
                          isDisabled ? 'Enable filter' : 'Disable filter'
                        }
                        icon={
                          isDisabled ? (
                            <VisibilityIcon />
                          ) : (
                            <VisibilityOffIcon />
                          )
                        }
                        onClick={ghost.onToggleDisabled}
                      />
                    )}
                    <Tooltip content="Remove">
                      <CardActionButton
                        type="button"
                        $variant="danger"
                        aria-label="Remove filter"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (isDisabled) {
                            ghost?.onRemoveGhost()
                          } else {
                            handleDeleteFilter()
                          }
                        }}
                      >
                        ×
                      </CardActionButton>
                    </Tooltip>
                  </CardActions>
                </RevealOnCardHover>
              )}
            </HeaderGroup>
          </div>
          <CardBody $disabled={isDisabled} $superseded={isSuperseded}>
            {isRangeWindow ? (
              <>
                <FilterChip $disabled $color="#6b7280" $nowrap>
                  {selectedOperator}
                </FilterChip>
                <FilterChip
                  $open={isPopoverOpen}
                  $disabled={!isValueInteractive}
                  onClick={
                    isValueInteractive
                      ? () => setIsPopoverOpen(!isPopoverOpen)
                      : undefined
                  }
                >
                  {customRangeText}
                </FilterChip>
              </>
            ) : isWindowFilter ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0px',
                  width: '100%',
                }}
              >
                {/* Row 1: is [TYPE] */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    position: 'relative',
                  }}
                >
                  <span
                    style={{
                      color: '#6b7280',
                      padding: '4px 8px',
                      fontSize: '12px',
                      WebkitUserSelect: 'none',
                      userSelect: 'none',
                    }}
                  >
                    {selectedOperator}
                  </span>
                  <FilterChip
                    $open={showRatingTypePopover}
                    $disabled={!isValueInteractive}
                    onClick={
                      isValueInteractive
                        ? () => {
                            if (!showRatingTypePopover) {
                              setIsPopoverOpen(false)
                              setIsOperatorDropdownOpen(false)
                            }
                            setShowRatingTypePopover(!showRatingTypePopover)
                          }
                        : undefined
                    }
                  >
                    {ratingTypeLabel}
                  </FilterChip>
                </div>
                {/* Row 2: and [RANGE] */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                  }}
                >
                  <span
                    style={{
                      color: '#6b7280',
                      padding: '4px 8px',
                      fontSize: '12px',
                      WebkitUserSelect: 'none',
                      userSelect: 'none',
                    }}
                  >
                    and
                  </span>
                  <FilterChip
                    $open={isPopoverOpen}
                    $disabled={!isValueInteractive}
                    onClick={
                      isValueInteractive
                        ? () => {
                            if (!isPopoverOpen) {
                              setIsOperatorDropdownOpen(false)
                              setShowRatingTypePopover(false)
                            }
                            setIsPopoverOpen(!isPopoverOpen)
                          }
                        : undefined
                    }
                  >
                    {ratingRangeText}
                  </FilterChip>
                </div>
                {includesNoStarReviews && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                    }}
                  >
                    <span
                      style={{
                        color: '#6b7280',
                        padding: '4px 8px',
                        fontSize: '12px',
                        WebkitUserSelect: 'none',
                        userSelect: 'none',
                      }}
                    >
                      and
                    </span>
                    <span
                      style={{
                        color: '#374151',
                        padding: '4px 8px',
                        fontSize: '12px',
                        WebkitUserSelect: 'none',
                        userSelect: 'none',
                      }}
                    >
                      Reviews with no star rating
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <>
                <FilterChip
                  ref={operatorRef}
                  $open={isOperatorDropdownOpen}
                  $disabled={!isOperatorInteractive}
                  $color="#6b7280"
                  $nowrap
                  onClick={
                    isOperatorInteractive
                      ? () => {
                          if (!isOperatorDropdownOpen) {
                            setIsPopoverOpen(false)
                          }
                          setIsOperatorDropdownOpen(!isOperatorDropdownOpen)
                        }
                      : undefined
                  }
                >
                  {selectedOperator}
                </FilterChip>
                <FilterChip
                  $open={isPopoverOpen}
                  $disabled={!isValueInteractive}
                  style={{ width: '100%' }}
                  // Hover is tracked here (in addition to the CSS `:hover`
                  // FilterChip already paints) solely to gate the "click to
                  // select filter values" tooltip below.
                  onMouseEnter={
                    isValueInteractive
                      ? () => setIsFieldValueHovered(true)
                      : undefined
                  }
                  onMouseLeave={() => setIsFieldValueHovered(false)}
                  onClick={
                    isValueInteractive
                      ? () => {
                          if (filter.filter_type === FilterTypes.SELECT) {
                            if (!isPopoverOpen) {
                              setIsOperatorDropdownOpen(false)
                            }
                            setIsPopoverOpen(!isPopoverOpen)
                          }
                        }
                      : undefined
                  }
                >
                  {getFieldValue(filter)}
                </FilterChip>
              </>
            )}
          </CardBody>
        </div>
      </div>

      {/* Tooltip on field value hover */}
      {isFieldValueHovered && filter.filter_type === FilterTypes.SELECT && (
        <div
          style={{
            position: 'absolute',
            left: '-200px',
            top: '0',
            width: '180px',
            backgroundColor: '#333',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            {filter.alias || filter.field}
          </div>
          <div style={{ opacity: 0.9 }}>Click to select filter values.</div>
        </div>
      )}

      {/* Operator Popover */}
      {isOperatorDropdownOpen && isOperatorInteractive && (
        <OperatorPopover
          onOperatorSelect={handleOperatorChange}
          onClose={() => setIsOperatorDropdownOpen(false)}
          selectedOperator={selectedOperator as OperatorType}
          triggerRef={operatorRef}
          cardRef={cardRef}
        />
      )}

      {isRangeWindow && (
        <FilterRangePopover
          isOpen={isPopoverOpen && isValueInteractive}
          onClose={() => setIsPopoverOpen(false)}
          cardRef={cardRef}
          bounds={{
            min:
              (filter as FilterOptionRange).cur_min ??
              (filter as FilterOptionRange).min,
            max:
              (filter as FilterOptionRange).cur_max ??
              (filter as FilterOptionRange).max,
          }}
          committed={{ min: currentSelection?.min, max: currentSelection?.max }}
          // Always `is`: the operator chip is static for a range.
          onApply={(range) =>
            updateFilterSelection(filter.field, 'is', [], range)
          }
          onClear={handleDeleteFilter}
        />
      )}

      {isRatingWindow && (
        <RatingTypePopover
          isOpen={showRatingTypePopover && isValueInteractive}
          selectedValueType={currentSelection?.valueType ?? 0}
          onSelect={handleRatingTypeChange}
          onClose={() => setShowRatingTypePopover(false)}
          cardRef={cardRef}
        />
      )}

      {(filter.filter_type === FilterTypes.SELECT ||
        (isWindowFilter && syntheticSelectFilter)) && (
        <FilterPopover
          filter={
            isWindowFilter && syntheticSelectFilter
              ? syntheticSelectFilter
              : popoverFilter
          }
          isOpen={isPopoverOpen && isValueInteractive}
          onClose={() => setIsPopoverOpen(false)}
          onSelectionChange={
            isWindowFilter ? handleWindowSelectionChange : handleSelectionChange
          }
          currentSelectedValues={
            isWindowFilter ? windowSelectedValues : selectedValues
          }
          cardRef={cardRef}
          disableSelectedFirstSort={isWindowFilter}
        />
      )}
    </CardRoot>
  )
}

export type { SelectedFilterOption }
